import React, { useContext, useEffect, useRef, useState, useMemo } from 'react';
import ParcelInfo from 'components/generics/Map/ParcelInfo';
import { Feature } from 'ol';
import { getCenter } from 'ol/extent';
import { Polygon } from 'ol/geom';
import { Point } from 'ol/geom.js';
import { Vector as VectorLayer } from 'ol/layer.js';
import { Vector as VectorSource } from 'ol/source.js';
import { ExploitationContext } from 'utils/context';
import {
  createNewMap,
  getPopupHeight,
  gpsToPixel,
  handleMouseMoveEvent,
  setPolygonStyle,
  zoomToFit,
} from './MapCommon';
import { emptyCulture } from 'utils/culturesColors';
import './Map.style.scss';
import MapTab from './MapTab';
import { getCultureCampaignKeyFromYear } from 'utils/Datagrid/CustomParcelstDataGridUtils';
import { ORGANIC_STATES } from 'utils/GlobalVariables';

function MapParcels({
  updateParcel,
  parcelUpdated,
  centerParcels,
  forecastCampaign,
  name = 'map',
  fromProcedure = false,
  parcelListData,
}) {
  const { exploitationContext } = useContext(ExploitationContext);
  const { culturesColors } = useContext(ExploitationContext);
  const [year, setYear] = useState(forecastCampaign.startYear);
  const [minYear, setMinYear] = useState(forecastCampaign.startYear - 1);
  const [maxYear, setMaxYear] = useState(forecastCampaign.startYear);
  const [parcelInfo, setParcelInfo] = useState(null);
  const [modalCoords, setModalCoords] = useState(null);
  const [storeParcelColor, setStoreParcelColor] = useState([]);
  const [cultureHoverName, setCultureHoverName] = useState(null);
  const [parcelHoverCultureName, setParcelHoverCultureName] = useState(null);
  const [mapObject, setMap] = useState();
  const [polygonSource, setPolygonSource] = useState();
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const mapElement = useRef();
  const popup = useRef(null);
  const popupContent = useRef(null);
  let activePolygon;
  const parcelsData = useMemo(() => {
    if (fromProcedure) {
      return parcelListData || [];
    }
    return exploitationContext?.parcels || [];
  }, [fromProcedure, parcelListData, exploitationContext?.parcels]);
  const data = parcelsData;
  const map = useMemo(() => mapObject, [mapObject]);

  useEffect(() => {
    return handleMouseMoveEvent(setCoords);
  }, []);

  useEffect(() => {
    setMap(createNewMap(name));
  }, []);

  useEffect(() => {
    if (map) {
      setMapDetails(map);
      map.once('rendercomplete', function (event) {
        zoomToFit(map);
      });
    }
  }, [map, centerParcels]);

  useEffect(() => {
    if (map) {
      setMapDetails(map);
    }
  }, [year, parcelUpdated, parcelsData]);

  useEffect(() => {
    if (map && cultureHoverName) {
      const extent = map.getView().calculateExtent(map.getSize());
      if (polygonSource) {
        polygonSource.forEachFeatureInExtent(extent, function (feature) {
          const selectedCultureName = parcelsData.find(
            (parcel) => parcel.id + '' + year + '' + parcel.name === feature.getId()
          );
          let parcelCultureAtYear =
            selectedCultureName?.parcelCultures[getCultureCampaignKeyFromYear(year)];
          if (!parcelCultureAtYear?.name) {
            parcelCultureAtYear = { ...emptyCulture, year: year };
          }
          if (selectedCultureName && parcelCultureAtYear) {
            if (parcelCultureAtYear?.name === cultureHoverName || cultureHoverName === 'null') {
              setPolygonStyle(
                feature,
                parcelCultureAtYear?.name,
                1,
                culturesColors,
                selectedCultureName?.organicState,
                selectedCultureName?.conversionDate,
                year
              );
            } else {
              setPolygonStyle(
                feature,
                parcelCultureAtYear?.name,
                0.3,
                culturesColors,
                selectedCultureName?.organicState,
                selectedCultureName?.conversionDate,
                year
              );
            }
          }
        });
      }
    }
  }, [map, cultureHoverName]);

  function setMapDetails(map) {
    handleMouseMoveEvent(setCoords);

    if (map && year) {
      map
        .getLayers()
        .getArray()
        .filter((layer) => layer.get('id') === 'polygonVector')
        .forEach((layer) => map.removeLayer(layer));

      const polygonSourceEffect = new VectorSource();
      parcelsData
        .filter((parcel) => parcel.coordinates)
        .map((parcel) => {
          let parcelCultureAtYear = parcel?.parcelCultures[getCultureCampaignKeyFromYear(year)];
          if (!parcelCultureAtYear?.name) {
            parcelCultureAtYear = { ...emptyCulture, year: year };
          }

          const parcelFeature = new Feature({
            geometry: new Polygon(gpsToPixel(parcel.coordinates)),
          });
          const parcelIdByYear = parcel.id + '' + year + '' + parcel.name;
          parcelFeature.setId(parcelIdByYear);
          parcelFeature.set('parcelId', parcel.id);
          setPolygonStyle(
            parcelFeature,
            activePolygon?.name ? activePolygon.name : parcelCultureAtYear?.name,
            1,
            culturesColors,
            parcel?.organicState,
            parcel?.conversionDate,
            year
          );

          let finalColorTable = storeParcelColor;
          finalColorTable.forEach((parcelColor, index) => {
            if (parcelColor.id === parcelIdByYear) {
              finalColorTable.splice(index, 1);
            }
          });
          finalColorTable.push({
            id: parcelIdByYear,
            culture: parcelCultureAtYear?.name,
          });

          setStoreParcelColor(finalColorTable);
          polygonSourceEffect.addFeature(parcelFeature);
          setPolygonSource(polygonSourceEffect);
          return polygonSourceEffect;
        });

      const geometryFunction = function () {
        polygonSourceEffect.getFeatures().forEach((feature) => {
          return new Point(getCenter(feature.getGeometry().getExtent()));
        });
      };

      const polygonVector = new VectorLayer({
        id: 'polygonVector',
        source: polygonSourceEffect,
        geometry: geometryFunction(),
      });

      map.addLayer(polygonVector);
      map.set();

      let selected = null;
      map.on('pointermove', function (e) {
        const extent = map.getView().calculateExtent(map.getSize());
        if (selected !== null) {
          setParcelInfo(null);
          setModalCoords(null);
          polygonSourceEffect.forEachFeatureInExtent(extent, function (feature) {
            const selectedCultureName = storeParcelColor.find(
              (parcel) => parcel.id === feature.getId()
            );
            const parcel = parcelsData.find(
              (parcel) => parcel.id + '' + year + '' + parcel.name === feature.getId()
            );

            if (selectedCultureName && selectedCultureName.culture) {
              setPolygonStyle(
                feature,
                selectedCultureName.culture,
                1,
                culturesColors,
                parcel?.organicState,
                parcel?.conversionDate,
                year
              );
            }
            setParcelHoverCultureName(null);
          });
          selected = null;
        }

        map.forEachFeatureAtPixel(e.pixel, function (f) {
          selected = f;
          setParcelInfo(
            parcelsData.find((parcel) => parcel.id + '' + year + '' + parcel.name === f.getId())
          );
          setModalCoords(e.pixel);

          polygonSourceEffect.forEachFeatureInExtent(extent, function (feature) {
            const selectedCultureName = storeParcelColor.find(
              (parcel) => parcel.id === feature.getId()
            );
            const parcel = parcelsData.find(
              (parcel) => parcel.id + '' + year + '' + parcel.name === feature.getId()
            );
            if (selectedCultureName && selectedCultureName.culture) {
              if (feature.getId() !== selected.getId()) {
                setPolygonStyle(
                  feature,
                  selectedCultureName.culture,
                  0.3,
                  culturesColors,
                  parcel?.organicState,
                  parcel?.conversionDate,
                  year
                );
              } else {
                setParcelHoverCultureName(selectedCultureName.culture);
              }
            }
          });
          return true;
        });
      });

      map.on('click', function (e) {
        map.forEachFeatureAtPixel(e.pixel, function (feature) {
          updateParcel(feature.get('parcelId'));
        });
      });
    }
  }

  const handleCultureHover = (cultureHoverName) => {
    setCultureHoverName(cultureHoverName);
  };

  const hasParcelOrganic = parcelsData.some(
    (parcel) =>
      parcel?.organicState === ORGANIC_STATES.organic ||
      parcel?.organicState === ORGANIC_STATES.conversion
  );

  return (
    <>
      {hasParcelOrganic && (
        <div className="legend_map_container" style={{ margin: '0 80px' }}>
          <div className="legend_title">Légende des contours de parcelle</div>
          <div className="legend_map_row">
            <div className="outline_legend">
              <div className="stroke conversion"></div>
              <p>Culture bio en conversion</p>
            </div>
            <div className="outline_legend">
              <div className="stroke organic"></div>
              <p>Culture bio</p>
            </div>
            <div className="outline_legend">
              <div className="stroke conventional"></div>
              <p>Culture conventionnelle</p>
            </div>
          </div>
        </div>
      )}
      <div className="tab_container">
        {!fromProcedure && (
          <div className="details_container">
            <MapTab
              data={data}
              header={['', 'Culture', 'Surface']}
              isDynamic={true}
              year={year}
              minYear={minYear}
              maxYear={maxYear}
              setYear={setYear}
              parcelHoverCultureName={parcelHoverCultureName}
              parentCallback={handleCultureHover}
              fromParcels
            />
          </div>
        )}
        <div ref={mapElement} className="map_container">
          <div
            id={name}
            className="map_element"
            onMouseOut={() => {
              setParcelInfo(null);
              setModalCoords(null);
            }}
          ></div>
          {parcelInfo && (
            <div className="parcel-info-modal" ref={popup}>
              {modalCoords && (
                <div
                  ref={popupContent}
                  id="popup"
                  className="ol-popup"
                  style={{
                    marginLeft: coords.x - 50 + 'px',
                    marginTop: coords.y - getPopupHeight(popupContent) - 30 + 'px',
                  }}
                >
                  <ParcelInfo
                    parcelInfo={parcelInfo}
                    from="mapParcels"
                    forecastCampaign={forecastCampaign}
                  ></ParcelInfo>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default MapParcels;
